import type { LeadStatus } from './types';

/**
 * Valid status transitions for a catering lead.
 *
 * Mirrors the backend state machine exactly (LEAD_TRANSITIONS in
 * transition-lead-status.dto.ts). Keep the two in sync — anything allowed here
 * but not there is accepted optimistically by the UI and then rejected by the
 * API with 422, which makes the card snap back after the fact.
 *
 * Rules:
 *  - Leads only move forward. The single permitted backward step is
 *    negotiating → estimate_sent (a revised estimate goes back out).
 *  - `won` and `lost` are terminal: no transitions out of either.
 */
export const VALID_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  new: ['contacted', 'estimate_sent', 'lost'],
  contacted: ['estimate_sent', 'negotiating', 'lost'],
  estimate_sent: ['negotiating', 'won', 'lost'],
  negotiating: ['estimate_sent', 'won', 'lost'],
  won: [],
  lost: [],
};

const LEAD_STATUSES: LeadStatus[] = Object.keys(VALID_TRANSITIONS) as LeadStatus[];

/**
 * Coerces a raw API status string into a known LeadStatus, defaulting to
 * 'new' for null/undefined/empty/unrecognized values (e.g. a lead created
 * without a status ever being set).
 *
 * Must be used wherever a lead's status is read off the API response —
 * otherwise a value like '' or an unmapped string passes the `?? 'new'`
 * cast used elsewhere (since `??` only catches null/undefined), leaving
 * `status` as that raw value. The status badge falls back to displaying
 * "New" for any unrecognized status, but VALID_TRANSITIONS[status] would
 * be undefined, so the Change Status menu silently renders with zero
 * options while the badge still reads "New".
 */
export function normalizeLeadStatus(status: string | null | undefined): LeadStatus {
  return status && (LEAD_STATUSES as string[]).includes(status) ? (status as LeadStatus) : 'new';
}
